// Focus management shared by the layered components: composite focus groups (data-dws-focus-group), the pointer
// opener stand-in for Safari, the dialog focus trap and focus return, menu focus, DwsPanel's keep-focus and the
// arrow-key groups of toolbars and panel tabs. Core: document-level behaviour that dialogs, drawers, menus, radio
// groups and toolbars rely on without calling anything first. (Moving and resizing dialogs: module "dialogdrag".)

import { publish } from "../shared/publish";

/** What dwsDialogFocus / dwsMenuFocus keep on their container while it is open. */
interface DwsFocusReturnState {
    returnTo: HTMLElement | null;
    fallbacks?: HTMLElement[];
    keyHandler?: (e: KeyboardEvent) => void;
    observer?: MutationObserver;
}

// Composite focus (DwsRadioButtonGroup, ...): an element marked data-dws-focus-group receives a synthetic, non-bubbling
// "focus" when focus enters it from outside and "blur" when focus leaves it entirely - moving between its own
// children raises neither. Blazor's FocusEventArgs has no relatedTarget, so the server cannot tell those moves apart
// from the per-child focus/blur events itself; here the browser knows. Blazor handles non-bubbling events through a
// capturing document listener, so an @onfocus/@onblur on the marked element receives these like native ones. The
// attribute is only rendered while the component has a handler bound, so unmarked groups cost nothing.
const dwsFocusGroupListener = function (e: FocusEvent): void {
    if (!(e.target instanceof Element)) {
        return;
    }

    const other = e.relatedTarget instanceof Node ? e.relatedTarget : null;
    const type = e.type === "focusin" ? "focus" : "blur";

    // Nested groups: every marked ancestor the focus crosses gets its own event. (No optional chaining here -
    // the bundler's minifier does not understand it.)
    let group = (e.target as Element).closest("[data-dws-focus-group]");

    while (group && !(other && group.contains(other))) {
        group.dispatchEvent(new FocusEvent(type, { relatedTarget: other }));
        group = group.parentElement ? group.parentElement.closest("[data-dws-focus-group]") : null;
    }
};

document.addEventListener("focusin", dwsFocusGroupListener, true);
document.addEventListener("focusout", dwsFocusGroupListener, true);

// Safari (macOS and iOS) never focuses a button or link that is clicked or tapped, so document.activeElement is still
// <body> when a dialog, drawer or overlay opens from one and there is no opener to hand focus back to. The last
// focusable element under a pointer press stands in for it, for a short while only (an unrelated click minutes ago is
// not an opener).
publish("dwsPointerOpener", (function () {
    const selector = 'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"]), [role="button"]';
    let last = null;
    let time = 0;

    document.addEventListener('pointerdown', function (e) {
        const target = e.target && (e.target as Element).closest ? (e.target as Element).closest(selector) : null;
        last = target;
        time = Date.now();
    }, true);

    return {
        get: function () {
            if (!last || Date.now() - time > 3000 || !document.contains(last)) {
                return null;
            }

            return last;
        }
    };
})());

// DwsDialog: a modal keeps keyboard focus inside itself and, once it is gone, gives focus back to whatever opened it
// (unless the user has moved focus elsewhere meanwhile). The removal is noticed by a MutationObserver, so nothing has
// to call in from the component's disposal - which may run after the circuit is gone. Stacked dialogs each remember
// the dialog below them.
publish("dwsDialogFocus", {
    selector: 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, audio[controls], video[controls], summary, [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])',

    focusables: function (container) {
        const surface = container.querySelector('[aria-modal="true"]') || container;
        const all = surface.querySelectorAll(this.selector);
        const result = [];

        for (let i = 0; i < all.length; i++) {
            const el = all[i];

            if (el.closest('[inert]') || el.getClientRects().length === 0 || getComputedStyle(el).visibility === 'hidden') {
                continue;
            }

            result.push(el);
        }

        return result;
    },

    open: function (containerId: string): void {
        const container = document.getElementById(containerId);

        if (!container || container._dwsDialogFocus) {
            return;
        }

        const self = this;
        const active = document.activeElement as HTMLElement | null;
        let returnTo = active && active !== document.body && !container.contains(active) ? active : null;

        // Safari (macOS and iOS) does not focus a button that is clicked or tapped, so the opener is not the active
        // element (the body, or a focused container around it such as the dialog below): fall back to the control
        // pressed last, if it is recent and still in the page.
        if ((!returnTo || (self._pressed && returnTo.contains(self._pressed.element)))
            && self._pressed && Date.now() - self._pressed.time < 10000
            && document.contains(self._pressed.element) && !container.contains(self._pressed.element)) {
            returnTo = self._pressed.element;
        }

        // Otherwise the shared pointer-press tracker (dwsPointerOpener), which also sees presses on non-focusable roles.
        if (!returnTo && window.dwsPointerOpener) {
            const opener = window.dwsPointerOpener.get();
            returnTo = opener && !container.contains(opener) ? opener : null;
        }

        const state: DwsFocusReturnState = { returnTo: returnTo, fallbacks: [] };

        // Initial focus. The dialog focuses its surface (role="dialog") from .NET right after this call, which makes
        // assistive technology announce it. An element inside marked data-dws-dialog-initial-focus (or autofocus) - a
        // form's first field, a confirmation's Cancel button - takes the focus from the surface that one time.
        const surfaceElement = container.querySelector('[aria-modal="true"]');

        if (surfaceElement) {
            const redirect = function (e) {
                if (e.target !== surfaceElement) {
                    return;
                }

                surfaceElement.removeEventListener('focus', redirect);

                const marked = surfaceElement.querySelectorAll<HTMLButtonElement>('[data-dws-dialog-initial-focus], [autofocus]');

                for (let i = 0; i < marked.length; i++) {
                    const candidate = marked[i];

                    if (!candidate.disabled && candidate.getAttribute('aria-disabled') !== 'true' && candidate.getClientRects().length > 0) {
                        candidate.focus({ preventScroll: true });
                        return;
                    }
                }

                // DwsDialogOptions.InitialFocus = FirstControl: the first control of the content or the actions, not
                // the close button of the title bar.
                if (surfaceElement.getAttribute('data-dws-dialog-focus') === 'first') {
                    const controls = self.focusables(container);

                    for (let i = 0; i < controls.length; i++) {
                        if (!controls[i].closest('.dws-dialog-title') && controls[i].getAttribute('aria-disabled') !== 'true') {
                            controls[i].focus({ preventScroll: true });
                            return;
                        }
                    }
                }
            };

            surfaceElement.addEventListener('focus', redirect);
        }

        // The opener can be gone when the dialog closes (a log entry trimmed from its list while its details were open):
        // then focus goes to the nearest ancestor that was there at open and still is, and that a component marked as
        // a fallback (data-dws-focus-fallback, with a tabindex) - not to the body, the top of the page.
        for (let el = returnTo ? returnTo.parentElement : null; el && el !== document.body; el = el.parentElement) {
            if (el.hasAttribute("data-dws-focus-fallback")) {
                state.fallbacks.push(el);
            }
        }

        state.keyHandler = function (e) {
            if (e.key !== 'Tab' || e.defaultPrevented) {
                return;
            }

            const items = self.focusables(container);

            if (items.length === 0) {
                e.preventDefault();
                (container.querySelector<HTMLElement>('[aria-modal="true"]') || container).focus({ preventScroll: true });
                return;
            }

            const first = items[0];
            const last = items[items.length - 1];
            const current = document.activeElement;
            // The modal surface itself (focused on open) counts as outside: Shift+Tab from it would otherwise leave the dialog.
            const inside = current && current !== container && current.getAttribute('aria-modal') !== 'true' && container.contains(current);

            if (e.shiftKey && (!inside || current === first)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (!inside || current === last)) {
                e.preventDefault();
                first.focus();
            }
        };

        container.addEventListener('keydown', state.keyHandler);

        const parent = container.parentNode;

        if (parent && typeof MutationObserver === 'function') {
            state.observer = new MutationObserver(function () {
                if (document.contains(container)) {
                    return;
                }

                state.observer.disconnect();
                container.removeEventListener('keydown', state.keyHandler);

                const now = document.activeElement;
                const target = state.returnTo;

                if (target && document.contains(target) && (!now || now === document.body)) {
                    try {
                        target.focus({ preventScroll: true });
                    } catch (err) {
                        // The opener cannot take focus any more: leave it where the browser put it.
                    }
                } else if (target && !document.contains(target) && (!now || now === document.body)) {
                    const fallback = state.fallbacks.find(function (el) { return document.contains(el); });

                    if (fallback) {
                        fallback.focus({ preventScroll: true });
                    }
                }
            });

            state.observer.observe(parent, { childList: true });
        }

        container._dwsDialogFocus = state;
    }
});

const dwsDialogFocusPressListener = function (e: PointerEvent): void {
    const target = e.target && (e.target as Element).closest ? (e.target as Element).closest(window.dwsDialogFocus.selector) : null;

    if (target) {
        window.dwsDialogFocus._pressed = { element: target, time: Date.now() };
    }
};

document.addEventListener("pointerdown", dwsDialogFocusPressListener, true);

// An Escape pressed right after a DwsDialog opened, before its surface took focus (on Blazor Server that is two round
// trips after the first render), went to the page behind and was lost: the dialog's Escape handler sits on its own
// container. While focus is still outside the newest open dialog - on the page or on the opener behind the modal, not
// inside another popup or menu - the key is handed to that dialog instead.
const dwsDialogEarlyEscapeListener = function (e: KeyboardEvent): void {
    if (e.key !== "Escape" || e.defaultPrevented || !e.isTrusted) {
        return;
    }

    const dialogs = document.querySelectorAll<HTMLElement>('[data-dws-dialog][aria-modal="true"]');
    const dialog = dialogs.length > 0 ? dialogs[dialogs.length - 1] : null;
    if (!dialog || dialog.getClientRects().length === 0) {
        return;
    }

    const active = document.activeElement as HTMLElement | null;
    if (active && dialog.contains(active)) {
        return;
    }

    // A dialog opened from another dialog: the opener sits in the lower one, and the key belongs to the new one.
    const insideLowerDialog = !!active && !!active.closest("[data-dws-dialog]");
    if (active && active !== document.body && !insideLowerDialog
        && active.closest('[role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"], .dws-popover')) {
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    try {
        dialog.focus({ preventScroll: true });
    } catch (err) {
        // A surface that cannot take focus still gets the key below.
    }

    dialog.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true }));
};

document.addEventListener("keydown", dwsDialogEarlyEscapeListener, true);

// Context menus (DwsTreeTable, DwsDataGrid, DwsTreeview): on open, focus moves to the first enabled item and the arrow
// keys, Home and End move between the items; once the menu is removed, focus returns to what had it before (the row
// that was right-clicked), unless the user has moved it elsewhere. Escape is handled by the component, which removes
// the menu. Identified by element id so a component without an ElementReference for the menu can use it.
publish("dwsMenuFocus", {
    open: function (menuId: string): void {
        const menu = document.getElementById(menuId);

        if (!menu || menu._dwsMenuFocus) {
            return;
        }

        const active = document.activeElement as HTMLElement | null;
        let returnTo = active && active !== document.body && !menu.contains(active) ? active : null;
        const pressed = window.dwsDialogFocus ? window.dwsDialogFocus._pressed : null;

        // Safari does not focus what is clicked: fall back to the control pressed last (see dwsDialogFocus).
        if ((!returnTo || (pressed && returnTo.contains(pressed.element))) && pressed && Date.now() - pressed.time < 10000
            && document.contains(pressed.element) && !menu.contains(pressed.element)) {
            returnTo = pressed.element;
        }

        const state: DwsFocusReturnState = { returnTo: returnTo };

        const items = function () {
            return Array.prototype.filter.call(
                menu.querySelectorAll('[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'),
                function (el) { return !el.disabled && el.getAttribute('aria-disabled') !== 'true' && el.getClientRects().length > 0; });
        };

        state.keyHandler = function (e) {
            const list = items();

            if (list.length === 0) {
                return;
            }

            const index = list.indexOf(document.activeElement);
            let next = -1;

            if (e.key === 'ArrowDown') {
                next = index < 0 || index === list.length - 1 ? 0 : index + 1;
            } else if (e.key === 'ArrowUp') {
                next = index <= 0 ? list.length - 1 : index - 1;
            } else if (e.key === 'Home') {
                next = 0;
            } else if (e.key === 'End') {
                next = list.length - 1;
            }

            if (next >= 0) {
                e.preventDefault();
                list[next].focus();
            }
        };

        menu.addEventListener('keydown', state.keyHandler);

        const first = items()[0];

        if (first) {
            first.focus({ preventScroll: true });
        } else {
            if (!menu.hasAttribute('tabindex')) {
                menu.setAttribute('tabindex', '-1');
            }

            menu.focus({ preventScroll: true });
        }

        const parent = menu.parentNode;

        if (parent && typeof MutationObserver === 'function') {
            state.observer = new MutationObserver(function () {
                if (document.contains(menu)) {
                    return;
                }

                state.observer.disconnect();

                const now = document.activeElement;
                const target = state.returnTo;

                if (target && document.contains(target) && (!now || now === document.body)) {
                    try {
                        target.focus({ preventScroll: true });
                    } catch (err) {
                        // The opener cannot take focus any more.
                    }
                }
            });

            state.observer.observe(parent, { childList: true });
        }

        menu._dwsMenuFocus = state;
    }
});

// DwsPanel: called right before the panel collapses. Focus inside the body or footer would be dropped to the document
// when they are hidden, so it moves to the panel's own toggle button (not a nested panel's).
publish("dwsPanelKeepFocus", function (panel: HTMLElement | null): void {
    let active = document.activeElement;

    if (!panel || !panel.querySelector || !active || active === document.body) {
        return;
    }

    let body = panel.querySelector(':scope > .dws-panel__body');
    let footer = panel.querySelector(':scope > .dws-panel__footer');

    if (!((body && body.contains(active)) || (footer && footer.contains(active)))) {
        return;
    }

    let toggle = panel.querySelector<HTMLButtonElement>(':scope > .dws-panel__header .dws-panel__toggle');

    if (toggle && toggle.tagName !== 'BUTTON') {
        toggle = toggle.querySelector<HTMLButtonElement>('button') || toggle;
    }

    if (toggle && !toggle.disabled && typeof toggle.focus === 'function') {
        toggle.focus();
    }
});

// Arrow-key movement inside a DwsToolbar and between DwsPanel tabs. Both announce a composite widget (role="toolbar",
// role="tablist") whose items a keyboard user expects to reach with the arrow keys; they only had Tab. Tabs follow
// the focus (automatic activation), toolbar items only take focus. Keys typed into a field inside a toolbar are left
// alone, since the arrows move its caret.
publish("dwsArrowKeyGroups", (function () {
    function isTextEntry(element) {
        if (!element) {
            return false;
        }

        let tag = element.tagName;

        return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable
            || element.getAttribute('role') === 'combobox';
    }

    // Not usable either: an element that is there but hidden (the toolbar's "more" button while everything fits, an
    // item waiting in the closed overflow panel) or inside an inert subtree - focus() does nothing on those, so the
    // arrows would stop at them.
    function isUsable(element) {
        return !element.disabled && element.getAttribute('aria-disabled') !== 'true' && element.offsetParent !== null
            && !element.closest('[inert]') && getComputedStyle(element).visibility !== 'hidden';
    }

    function move(items, current, key, vertical) {
        let index = items.indexOf(current);

        if (index < 0) {
            return null;
        }

        let forward = vertical ? 'ArrowDown' : 'ArrowRight';
        let backward = vertical ? 'ArrowUp' : 'ArrowLeft';

        if (key === 'Home') {
            return items[0];
        }

        if (key === 'End') {
            return items[items.length - 1];
        }

        if (key === forward) {
            return items[(index + 1) % items.length];
        }

        if (key === backward) {
            return items[(index - 1 + items.length) % items.length];
        }

        return null;
    }

    // Every element of the toolbar that can take focus, including those the roving tabindex took out of the Tab order.
    // A DwsToolbar, or any group of buttons marked data-dws-arrow-group (the pickers' preset lists).
    let GROUP = '.dws-toolbar[role="toolbar"], [data-dws-arrow-group]';

    // Blazor disabling the item that holds tabindex 0 (a preset that stops being applicable) would leave the group
    // without a Tab stop, so the first usable item takes over.
    function watchTabStop(group) {
        if (group._dwsRovingWatch || typeof MutationObserver === 'undefined') {
            return;
        }

        // Also re-applies the roving state when Blazor writes a tabindex of its own (a control re-rendered with a
        // changed value took it back until the next focus inside the group, and was a second Tab stop until then) and
        // when an item is added. The writes below satisfy the rule they enforce, so the observer settles after one
        // further run.
        group._dwsRovingWatch = new MutationObserver(function () {
            let items = toolbarItems(group).filter(function (element) { return !isTextEntry(element); });

            if (items.length === 0) {
                return;
            }

            let current = group._dwsRovingCurrent;

            if (!current || items.indexOf(current) < 0) {
                current = items.filter(function (element) { return element.getAttribute('tabindex') === '0'; })[0] || items[0];
                group._dwsRovingCurrent = current;
            }

            items.forEach(function (element) {
                let wanted = element === current ? '0' : '-1';

                if (element.getAttribute('tabindex') !== wanted) {
                    element.setAttribute('data-dws-roving', '');
                    element.setAttribute('tabindex', wanted);
                }
            });
        });

        group._dwsRovingWatch.observe(group, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'tabindex', 'inert'] });
    }

    function toolbarItems(toolbar) {
        let focusable = toolbar.querySelectorAll('button, a[href], [tabindex], input, select, textarea');

        return Array.prototype.filter.call(focusable, function (element) {
            return isUsable(element) && element.closest(GROUP) === toolbar
                && (element.getAttribute('tabindex') !== '-1' || element.hasAttribute('data-dws-roving'));
        });
    }

    // Roving tabindex: a toolbar is one Tab stop (WAI-ARIA APG). Once focus is inside, the focused item keeps
    // tabindex 0 and the other items get -1 (marked data-dws-roving so they stay arrow-key targets), so Tab leaves
    // the toolbar and Shift+Tab / Tab back in returns to the last used item. Text fields stay Tab stops: the arrows
    // move their caret, so they could not be left otherwise. Done here rather than in .NET because the items are
    // arbitrary child content; Blazor only rewrites an attribute it renders itself when its own value changes.
    document.addEventListener('focusin', function (e) {
        let toolbar = e.target && (e.target as Element).closest ? (e.target as Element).closest(GROUP) : null;

        if (!toolbar) {
            return;
        }

        let items = toolbarItems(toolbar);
        let current = items.filter(function (element) { return element === e.target || element.contains(e.target); })[0];

        if (!current) {
            return;
        }

        items.forEach(function (element) {
            if (isTextEntry(element)) {
                return;
            }

            element.setAttribute('data-dws-roving', '');
            element.setAttribute('tabindex', element === current ? '0' : '-1');
        });

        toolbar._dwsRovingCurrent = isTextEntry(current) ? toolbar._dwsRovingCurrent : current;
        watchTabStop(toolbar);
    });

    document.addEventListener('keydown', function (e) {
        if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || !e.target || !(e.target as Element).closest) {
            return;
        }

        let tab = (e.target as Element).closest('.dws-panel__tabs > [role="tab"]');

        if (tab) {
            // Disabled tabs (aria-disabled) are skipped, as in DwsTabGroup. The focused tab stays in the list even when
            // it is disabled itself (the active tab that was disabled afterwards), so the arrows can leave it.
            let tabs = Array.prototype.filter.call(tab.parentElement.querySelectorAll(':scope > [role="tab"]'), function (element) {
                return element === tab || isUsable(element);
            });
            let nextTab = move(tabs, tab, e.key, false);

            if (nextTab) {
                e.preventDefault();
                nextTab.focus();
                nextTab.click();
            }

            return;
        }

        let toolbar = (e.target as Element).closest(GROUP);

        if (!toolbar || isTextEntry(e.target)) {
            return;
        }

        let items = toolbarItems(toolbar);
        let vertical = toolbar.getAttribute('aria-orientation') === 'vertical';

        // On-screen order: MoveItemUp/MoveItemDown reorder the items with the flex "order" property, not in the DOM, so
        // the arrows walked the old order after a reorder.
        items.sort(function (a, b) {
            let ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();

            if (vertical) {
                return (ra.top - rb.top) || (ra.left - rb.left);
            }

            return (Math.round(ra.top / 8) - Math.round(rb.top / 8)) || (ra.left - rb.left);
        });
        let current = items.filter(function (element) { return element === e.target || element.contains(e.target); })[0];
        // The items are in on-screen order, so Right/Left already follow the visual order in a right-to-left layout.
        let next = move(items, current, e.key, vertical);

        // A marked group (chips that wrap, or a column on wide screens) takes both arrow pairs.
        if (!next && toolbar.hasAttribute('data-dws-arrow-group')) {
            next = move(items, current, e.key, !vertical);
        }

        if (next) {
            e.preventDefault();
            next.focus();
        }
    });

    return {};
})());
